import React, { useState } from "react";
import { CheckCircle2, Send, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { SERVICES } from "@/lib/constants";
import { useSettings } from "@/context/SettingsContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const empty = { contact_name: "", business_name: "", email: "", phone: "", services: [], message: "", company_website: "" };

export default function PublicEnquiry() {
  const { settings } = useSettings();
  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const scrollToForm = () => {
    document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => document.querySelector('[data-testid="public-contact-name"]')?.focus(), 400);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.contact_name.trim() || !form.business_name.trim()) {
      setError("Please add your name and business name.");
      return;
    }
    setSubmitting(true);
    try {
      await api.publicEnquiry(form);
      setDone(true);
    } catch (err) {
      if (err?.response?.status === 429) setError("Too many submissions from your network. Please try again a little later.");
      else setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900" data-testid="public-enquiry-page">
      {/* Top brand bar */}
      <div className="border-b border-slate-800">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-5">
          <div className="grid h-10 w-10 place-items-center rounded-xl shadow-sm" style={{ backgroundColor: "hsl(var(--brand-accent))" }}>
            <svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2 4 5v6c0 5 3.5 8.5 8 11 4.5-2.5 8-6 8-11V5l-8-3Z" />
              <path d="M9 12l2 2 4-4" />
            </svg>
          </div>
          <div className="leading-tight">
            <div className="font-display text-sm font-extrabold tracking-tight text-white">{settings?.firm_name || "PRO ACCOUNTANTS"}</div>
            <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">{settings?.tagline || "London"}</div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 pb-28 pt-12 sm:pb-12">
        <div className="animate-fade-up">
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Get a quote for your business</h1>
          <p className="mt-2 max-w-xl text-sm text-slate-400">Tell us a little about your business and what you need. One of our ACCA-qualified team will be in touch within one working day.</p>
        </div>

        <div id="enquiry-form" className="animate-fade-up mt-8 rounded-2xl border border-slate-800 bg-white p-6 shadow-xl sm:p-8" style={{ animationDelay: "80ms" }}>
          {done ? (
            <div className="grid place-items-center py-14 text-center" data-testid="public-success">
              <div className="grid h-14 w-14 place-items-center rounded-full bg-emerald-50">
                <CheckCircle2 className="h-8 w-8 text-emerald-500" />
              </div>
              <h2 className="mt-4 font-display text-xl font-bold text-slate-900">Thank you — we've got it.</h2>
              <p className="mt-1 max-w-sm text-sm text-slate-500">Your enquiry has been received and added to our team's queue. We'll reach out to {form.email || "you"} shortly.</p>
              <Button className="mt-6" variant="outline" onClick={() => { setForm(empty); setDone(false); }} data-testid="public-another">Submit another enquiry</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-5">
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label>Your name *</Label>
                  <Input data-testid="public-contact-name" value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Jane Doe" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Business name *</Label>
                  <Input data-testid="public-business-name" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Doe Ltd" />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="grid gap-1.5">
                  <Label>Email</Label>
                  <Input data-testid="public-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@doe.co.uk" />
                </div>
                <div className="grid gap-1.5">
                  <Label>Phone</Label>
                  <Input data-testid="public-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="020 7946 0000" />
                </div>
              </div>
              <div className="grid gap-2">
                <Label>What do you need help with?</Label>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                  {SERVICES.map((s) => (
                    <label key={s} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50">
                      <Checkbox checked={form.services.includes(s)} onCheckedChange={() => toggleService(s)} data-testid={`public-service-${s.replace(/\s+/g, '-').toLowerCase()}`} />
                      <span>{s}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid gap-1.5">
                <Label>Message</Label>
                <Textarea data-testid="public-message" value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="Tell us about your business and what you're looking for…" />
              </div>
              {/* Honeypot — hidden from real users, catches bots */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                value={form.company_website}
                onChange={(e) => set("company_website", e.target.value)}
                style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                data-testid="public-honeypot"
              />
              {error && <p className="text-sm font-medium text-rose-600" data-testid="public-error">{error}</p>}
              <Button type="submit" disabled={submitting} data-testid="public-submit" style={{ backgroundColor: "hsl(var(--brand))" }} className="h-11 rounded-full text-white">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send enquiry
              </Button>
              <p className="text-center text-xs text-slate-400">We'll only use your details to respond to this enquiry.</p>
            </form>
          )}
        </div>
      </div>

      {!done && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-800 bg-slate-900/95 p-3 backdrop-blur sm:hidden" data-testid="mobile-quote-bar">
          <Button onClick={scrollToForm} data-testid="mobile-quote-cta" style={{ backgroundColor: "hsl(var(--brand-accent))" }} className="h-12 w-full rounded-full text-base font-semibold text-white">
            Get a quote
          </Button>
        </div>
      )}
    </div>
  );
}
